import { PageHeader } from '@/components/PageHeader';
import { StatCard } from '@/components/StatCard';
import { Badge } from '@/components/ui/badge';
import { useSession } from '@/features/auth/auth-store';
import { getOrgStats } from '@/features/org/org-stats-api';
import { RecentActivity } from '@/features/org/recent-activity';
import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import {
  ArrowUpRight,
  Database,
  FileSpreadsheet,
  KeyRound,
  type LucideIcon,
  Share2,
  Upload,
  Zap,
} from 'lucide-react';
import type { ReactElement } from 'react';
import { Trans, useTranslation } from 'react-i18next';

export const Route = createFileRoute('/app/')({
  component: DashboardPage,
});

function DashboardPage() {
  const { t } = useTranslation();
  const session = useSession();
  const orgId = session?.activeOrg.id;
  const { data, isLoading } = useQuery({
    queryKey: ['org-stats', orgId],
    queryFn: () => getOrgStats(orgId as string),
    enabled: Boolean(orgId),
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });
  if (!session) return null;
  const stats = data?.stats;

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow={t('app.dashboard.eyebrow', { defaultValue: 'Workspace' })}
        title={t('app.dashboard.welcomeTitle')}
        description={
          <Trans
            i18nKey="app.dashboard.welcomeBody"
            values={{ slug: session.activeOrg.slug, role: session.activeOrg.role }}
            components={{ strong: <span className="font-medium text-foreground" /> }}
          />
        }
        actions={
          <Badge variant="secondary" className="rounded-full px-3 py-1">
            <span className="size-1.5 rounded-full bg-emerald-500" aria-hidden />
            {t('app.dashboard.activeOrg', { defaultValue: 'Active org' })} ·{' '}
            <span className="font-semibold text-foreground">{session.activeOrg.slug}</span>
          </Badge>
        }
      />

      <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label={t('app.dashboard.stats.templates', { defaultValue: 'Templates' })}
          icon={FileSpreadsheet}
          loading={isLoading}
          value={stats?.templates}
        />
        <StatCard
          label={t('app.dashboard.stats.datasets', { defaultValue: 'Datasets' })}
          icon={Database}
          loading={isLoading}
          value={stats?.datasets}
        />
        <StatCard
          label={t('app.dashboard.stats.exports', { defaultValue: 'Exports this week' })}
          icon={Upload}
          loading={isLoading}
          value={stats?.exportsThisWeek}
        />
        <StatCard
          label={t('app.dashboard.stats.apiCalls', { defaultValue: 'API calls (24h)' })}
          icon={Zap}
          loading={isLoading}
          value={stats?.apiCalls24h}
        />
      </section>

      <section className="space-y-4">
        <div className="flex items-end justify-between">
          <div>
            <h2 className="text-lg font-semibold tracking-tight">
              {t('app.dashboard.quickActions.title', { defaultValue: 'Quick actions' })}
            </h2>
            <p className="text-sm text-muted-foreground">
              {t('app.dashboard.quickActions.body', {
                defaultValue: 'Jump straight back into your work.',
              })}
            </p>
          </div>
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <ActionCard
            to="/app/templates"
            icon={FileSpreadsheet}
            title={t('app.dashboard.cards.templates.title')}
            body={t('app.dashboard.cards.templates.body')}
          />
          <ActionCard
            to="/app/datasets"
            icon={Database}
            title={t('app.dashboard.cards.datasets.title')}
            body={t('app.dashboard.cards.datasets.body')}
          />
          <ActionCard
            to="/app/exports"
            icon={Upload}
            title={t('app.dashboard.cards.exports.title')}
            body={t('app.dashboard.cards.exports.body')}
          />
          <ActionCard
            to="/app/settings/google"
            search={{ google: undefined, reason: undefined }}
            icon={Share2}
            title={t('app.dashboard.cards.google.title')}
            body={t('app.dashboard.cards.google.body')}
          />
          <ActionCard
            to="/app/settings/api-keys"
            icon={KeyRound}
            title={t('app.dashboard.cards.apiKeys.title')}
            body={t('app.dashboard.cards.apiKeys.body')}
          />
        </div>
      </section>

      <section className="space-y-3">
        <h2 className="text-lg font-semibold tracking-tight">
          {t('app.dashboard.recent.title', { defaultValue: 'Recent activity' })}
        </h2>
        <RecentActivity orgId={session.activeOrg.id} />
      </section>
    </div>
  );
}

interface ActionCardProps {
  to: string;
  icon: LucideIcon;
  title: string;
  body: string;
  search?: Record<string, unknown>;
}

function ActionCard({ to, icon: Icon, title, body, search }: ActionCardProps): ReactElement {
  return (
    <Link
      to={to}
      {...(search ? { search: search as never } : {})}
      className="group relative flex flex-col gap-3 overflow-hidden rounded-xl border border-border/70 bg-card p-5 shadow-soft transition-all hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-elevate"
    >
      <div className="flex items-center justify-between">
        <span className="inline-flex size-10 items-center justify-center rounded-xl bg-primary/10 text-primary transition-transform group-hover:scale-105">
          <Icon className="size-5" aria-hidden />
        </span>
        <ArrowUpRight
          className="size-4 text-muted-foreground transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-primary"
          aria-hidden
        />
      </div>
      <div className="space-y-1">
        <h3 className="text-sm font-semibold tracking-tight">{title}</h3>
        <p className="text-sm leading-relaxed text-muted-foreground">{body}</p>
      </div>
      <span
        aria-hidden
        className="pointer-events-none absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-primary/40 to-transparent opacity-0 transition-opacity group-hover:opacity-100"
      />
    </Link>
  );
}
